import React, { useMemo } from 'react';
import {
  humaniseAge,
  readingStateLabel,
  Health,
  inHealth,
} from '../utils/meterFilters';

/**
 * Derives the fleet summary shown above the meter grid.
 *
 * Data issues counts the meters that are connected and sending values that
 * cannot be used. A plain Active/Inactive flag calls those healthy, which is
 * why they get a tile of their own.
 *
 * The tile is named for what it holds. A meter that is off is down whichever
 * end went quiet, so meter off and gateway off belong under Down, and what is
 * left here is the one state that means talking but not making sense.
 *
 * Reporting and data issues deliberately overlap: a meter can be sending every
 * minute and still be sending rubbish, and both facts matter.
 *
 * @param {Array<object>} meters - All meters returned by the API.
 * @param {Function} now - Clock, injectable so tests stay deterministic.
 * @returns {object} Counts and supporting detail for each tile.
 */
export const summariseMeters = (meters = [], now = () => new Date()) => {
  const reporting = meters.filter((meter) => inHealth(meter, Health.REPORTING));
  const down = meters.filter((meter) => inHealth(meter, Health.DOWN));

  const problems = meters.filter((meter) => inHealth(meter, Health.PROBLEMS));

  const newestSeen = reporting.reduce((newest, meter) => {
    if (!meter.lastSeenAt) return newest;
    const seen = new Date(meter.lastSeenAt).getTime();
    return seen > newest ? seen : newest;
  }, 0);

  const longestSilent = down.reduce((worst, meter) => {
    if (!meter.lastSeenAt) return worst;
    if (!worst?.lastSeenAt) return meter;
    return new Date(meter.lastSeenAt) < new Date(worst.lastSeenAt) ? meter : worst;
  }, null);

  const locations = new Set(
    meters.map((meter) => meter.sourceId).filter((id) => id !== undefined && id !== null)
  ).size;

  return {
    total: meters.length,
    reporting: reporting.length,
    down: down.length,
    problems: problems.length,
    problemDetail: problemDetail(problems),
    newestSeenLabel: newestSeen ? humaniseAge(new Date(newestSeen), now) : null,
    longestSilent: longestSilent
      ? {
          name: longestSilent.meterName,
          age: humaniseAge(longestSilent.lastSeenAt, now),
        }
      : null,
    downDetail: downDetail(down, now),
    locations,
  };
};

/**
 * How many names fit under a count before the line stops being readable.
 *
 * Past this the tile says how many are left and the reader clicks it, which is
 * a shorter route to the answer than a line that wraps to three rows.
 */
const NAMES_SHOWN = 2;

/**
 * Names the meters behind a count, and says how many it could not fit.
 *
 * A tile reading 3 that named one of them, or none, was a question the page
 * asked and would not answer. Shared by both tiles that name meters, so they
 * cannot drift into two ways of saying the same thing.
 *
 * @param {Array<object>} meters - The meters this tile counts, in the order to
 *   name them.
 * @param {Function} describe - Turns one meter into its phrase.
 * @param {string} whenEmpty - The line to show when the tile counts nothing.
 * @returns {string} A line for under the count.
 */
const namesUnder = (meters, describe, whenEmpty) => {
  if (!meters.length) return whenEmpty;

  const named = meters.slice(0, NAMES_SHOWN).map(describe).join(', ');
  const rest = meters.length - NAMES_SHOWN;

  return rest > 0 ? `${named} + ${rest} more` : named;
};

/**
 * The meters that are down, longest silent first — the one worth opening.
 * @param {Array<object>} down - Meters that are not reporting.
 * @param {Function} now - Clock, injectable so tests stay deterministic.
 * @returns {string} A line for under the count.
 */
const downDetail = (down, now) =>
  namesUnder(
    [...down].sort(
      (a, b) => new Date(a.lastSeenAt || 0) - new Date(b.lastSeenAt || 0)
    ),
    (meter) =>
      meter.lastSeenAt
        ? `${meter.meterName} · ${humaniseAge(meter.lastSeenAt, now)}`
        : meter.meterName,
    'All meters reporting'
  );

/**
 * The meters whose readings cannot be used, and what is wrong with each.
 *
 * The line used to read "2 invalid · 2 empty" — the API's own words for the
 * states, and the only place in the product still using them. The alert email
 * has called these a data issue and a meter being off for as long as it has
 * existed, and the meter cards say the same, so this says it too.
 *
 * @param {Array<object>} problems - Meters connected but unusable.
 * @returns {string} A line for under the count.
 */
const problemDetail = (problems) =>
  namesUnder(
    problems,
    (meter) => `${meter.meterName} · ${readingStateLabel(meter.readingState)}`,
    'All readings valid'
  );

/**
 * One tile in the health strip.
 * @param {string} label - Uppercase tile label.
 * @param {number} value - Count to display.
 * @param {string} sub - Supporting line beneath the count.
 * @param {string} tone - 'good', 'warn', 'bad', or omitted for neutral.
 * @param {boolean} selected - Whether the grid is currently narrowed to it.
 * @param {Function} onSelect - Narrows the grid to this tile, or widens it
 *   again. A tile without one is a fact rather than a control.
 */
const Stat = ({ label, value, sub, tone, selected, onSelect }) => {
  const className = `im-stat ${tone ? `im-stat--${tone}` : ''}${
    selected ? ' im-stat--on' : ''
  }`;

  if (!onSelect) {
    return (
      <div className={className}>
        <div className="im-stat-label">{label}</div>
        <div className="im-stat-value">{value}</div>
        <div className="im-stat-sub">{sub}</div>
      </div>
    );
  }

  return (
    <button type="button" className={className} aria-pressed={selected} onClick={onSelect}>
      <div className="im-stat-label">{label}</div>
      <div className="im-stat-value">{value}</div>
      <div className="im-stat-sub">{sub}</div>
    </button>
  );
};

/**
 * Fleet health summary shown above the meter grid, so the state of the estate
 * is readable without scanning every card.
 *
 * Each count is also the way to the meters behind it. A tile reading 3 that
 * named one meter and offered no route to the other two was a question the
 * page asked and would not answer.
 *
 * @param {Array<object>} meters - All meters returned by the API.
 * @param {string} health - The tile the grid is currently narrowed to.
 * @param {Function} onHealthChange - Told which tile to narrow to, or nothing
 *   to show the fleet again.
 */
const MeterHealthStrip = ({ meters, health, onHealthChange }) => {
  const summary = useMemo(() => summariseMeters(meters), [meters]);

  if (!summary.total) return null;

  /** Clicking the tile the grid is already narrowed to widens it again. */
  const select = (value) => () =>
    onHealthChange?.(value === health ? undefined : value);

  return (
    <div className="im-health">
      <Stat
        label="Reporting"
        value={summary.reporting}
        sub={summary.newestSeenLabel ? `Newest ${summary.newestSeenLabel} ago` : 'None reporting'}
        tone={summary.reporting ? 'good' : undefined}
        selected={health === Health.REPORTING}
        onSelect={onHealthChange && summary.reporting ? select(Health.REPORTING) : undefined}
      />
      <Stat
        label="Data issues"
        value={summary.problems}
        sub={summary.problemDetail}
        tone={summary.problems ? 'warn' : undefined}
        selected={health === Health.PROBLEMS}
        onSelect={onHealthChange && summary.problems ? select(Health.PROBLEMS) : undefined}
      />
      <Stat
        label="Down"
        value={summary.down}
        sub={summary.downDetail}
        tone={summary.down ? 'bad' : undefined}
        selected={health === Health.DOWN}
        onSelect={onHealthChange && summary.down ? select(Health.DOWN) : undefined}
      />
      <Stat
        label="Total meters"
        value={summary.total}
        sub={`Across ${summary.locations} location${summary.locations === 1 ? '' : 's'}`}
        selected={!health}
        onSelect={onHealthChange ? select(undefined) : undefined}
      />
    </div>
  );
};

export default MeterHealthStrip;
